import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../../components/text/ErrorMessage";
import {
  Phone,
  EmailAddress,
  Designation,
  Customer,
  isDirectorOfCompany,
  Department,
  Seniority,
  isVerified,
  contactStatus,
  addressType,
  customerContactType,
} from "../../../core/api/models";
import CustomerPhone from "../../../components/customer-phone";
import CustomerEmail from "../../../components/customer-email";
import { UploadUserImage } from "../../../components/upload";
import title from "../../../utils/title.json";
import Countries from "../../../utils/countries.json";
import States from "../../../utils/states.json";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../api";
import {
  CheckPhoneNumberEmpty,
  ValidateAlphabets,
  ValidateEmail,
  Validate965DigitPhoneNumber,
  ValidatePhoneNumberRegEx,
} from "../../../utils/Validatation";
import { useDataLoader } from "react-remote-data-hooks";
import PhoneInput from "../../../components/phone-input";
import DateInput from "../../../components/form-group/time-input/TimeInput";

interface LandLordForm {
  photo?: any;
  prefix?: string;
  firstName?: string;
  lastName?: string;
  phones?: Phone[];
  emails?: EmailAddress[];
  designationId?: string;
}

interface CompanyCustomerFields {
  customerContactType: string;
  isVerified: string;
  assistantName?: string;
  assistantNumber?: string;
  assistantDesignation?: string;
  seniority?: string;
  department?: string;
  associationStartDate?: string;
  associationEndDate?: string;
  isDirectorOfCompany?: string;
  accountManager?: string;
  addressType?: string;
  address?: string;
  country?: string;
  state?: string;
  district?: string;
  city?: string;
  postalCode?: string;
  contactStatus?: string;
  remarks?: string;
}
const gridColumnStyleDetails = {
  1: "col-12",
  2: "col-md-6 col-12",
  3: "col-md-4 col-12",
};
const AddNewUserModal: React.FunctionComponent<{
  error?: string;
  onCreateUser?: () => void;
  phones?: Phone[];
  emails?: EmailAddress[];
  onClose?: () => void;
  propertyId?: string;
  approvalType?: string;
  companyId?: string;
  gridColumns?: 1 | 2 | 3;
}> = (props) => {
  const gridColumnStyle: string = !!props.gridColumns
    ? gridColumnStyleDetails[props.gridColumns]
    : "col-12";
  const [isEmailEnable, setIsEmailEnable] = useState<boolean>(true);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const [selectedPhone, setSelectedPhone] = useState<Phone[] | undefined>(props.phones);
  const [selectedEmail, setSelectedEmail] = useState<EmailAddress[] | undefined>(props.emails);
  const [districtList, setDistrictList] = useState([]);
  const [isEmailOrPhone, setIsEmailOrPhone] = useState<boolean>(false);

  const { data: designations } = useDataLoader<Designation[]>(() =>
    api.designation.loadAllDesignation()
  );
  const { data: departmentDetails } = useDataLoader<Department[]>(() =>
    api.department.loadAllDepartments()
  );
  const { data: seniorityDetails } = useDataLoader<Seniority[]>(() =>
    api.seniority.loadAllSeniority()
  );

  const { control, handleSubmit, errors, clearErrors, watch, reset } = useForm<
    LandLordForm & CompanyCustomerFields
  >({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const statesList = watch("country") === "India" || !watch("country") ? States : [];

  const onSubmit = async (fdata?: any) => {
    const hasEmailOrPhone = () => {
      if (
        !selectedEmail &&
        selectedPhone &&
        selectedPhone?.every((x) => x?.phone === x?.dialCode || x?.phone === "")
      ) {
        setIsEmailOrPhone(true);
        return false;
      } else if (!selectedPhone && selectedEmail && selectedEmail?.every((sm) => sm.email === "")) {
        setIsEmailOrPhone(true);
        return false;
      } else if (
        selectedPhone &&
        selectedEmail &&
        selectedPhone?.every((x) => x.phone === x.dialCode || x.phone === "") &&
        selectedEmail?.every((sm) => sm.email === "")
      ) {
        setIsEmailOrPhone(true);
        return false;
      } else if (!selectedPhone && !selectedEmail) {
        setIsEmailOrPhone(true);
        return false;
      } else {
        setIsEmailOrPhone(false);
        return true;
      }
    };

    if (hasEmailOrPhone()) {
      setIsSubmiting(true);

      const landLordFormData: LandLordForm = {
        prefix: fdata?.prefix,
        firstName: fdata?.firstName,
        lastName: fdata?.lastName,
        phones: selectedPhone?.filter((x) => CheckPhoneNumberEmpty(x.phone)),
        emails: selectedEmail?.filter((x) => ValidateEmail(x.email)),
        photo: fdata.photo,
      };

      if (props?.approvalType === "Client") {
        try {
          const customerData: Customer = {
            prefix: fdata?.prefix,
            firstName: fdata?.firstName,
            lastName: fdata?.lastName,
            phones: selectedPhone?.filter((x) => CheckPhoneNumberEmpty(x.phone)),
            emails: selectedEmail?.filter((x) => ValidateEmail(x.email)),
            photo: fdata.photo,
            companyId: props?.companyId,
            customerContactType: fdata?.customerContactType,
            designationId: fdata?.designationId,
            isVerified: fdata?.isVerified,
            assistantName: fdata?.assistantName,
            assistantNumber: fdata?.assistantNumber,
            assistantDesignation: fdata?.assistantDesignation,
            seniority: fdata?.seniority,
            department: fdata?.department,
            associationStartDate: fdata?.associationStartDate,
            associationEndDate: fdata?.associationEndDate,
            isDirectorOfCompany: fdata?.isDirectorOfCompany,
            accountManager: fdata?.accountManager,
            remarks: fdata?.remarks,
          };

          const hasAddressDetails = [
            fdata?.addressType,
            fdata?.address,
            fdata?.country,
            fdata?.state,
            fdata?.district,
            fdata?.city,
            fdata?.postalCode,
            fdata?.contactStatus,
          ].every((value) => !value);

          if (!hasAddressDetails) {
            customerData.addresses = [
              {
                addressType: fdata?.addressType,
                country: fdata?.country,
                state: fdata?.state,
                district: fdata?.district,
                city: fdata?.city,
                address: fdata?.address,
                postalCode: fdata?.postalCode,
                isPrimary: false,
                contactStatus: fdata?.contactStatus,
              },
            ]
          }

          await api.customer.createCustomer(customerData);
          props?.onCreateUser?.();
          props?.onClose?.();
          toastr.success("Success", "User added successfully");
          setIsSubmiting(false);
        } catch (error) {
          toastr.error("Error", error?.response?.data?.message);
        }
      } else {
        try {
          await api.sitevisit.addNewLandLord(props?.propertyId!, landLordFormData);
          props?.onCreateUser?.();
          toastr.success("Success", "User added successfully");
          props?.onClose?.();
          setIsSubmiting(false);
        } catch (error) {
          toastr.error("Error", error?.response?.data?.message);
        }
      }
    }
  };

  const onChangePhone = (phoneNumber: Phone[]) => {
    setSelectedPhone(phoneNumber);
    setIsEmailOrPhone(false);
    clearErrors("phones");
  };

  const onChangeEmail = (emailAdd: EmailAddress[]) => {
    setSelectedEmail(emailAdd);
    setIsEmailOrPhone(false);
    clearErrors("emails");
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white ">
      <div className="p-4">
        <div className="row">
          <div className="form-group d-flex flex-wrap align-items-center col-12">
            <Controller
              control={control}
              name="photo"
              render={({ onChange }) => (
                <UploadUserImage id={"photo"} title={"Add Photo"} onChange={onChange} />
              )}
            />
          </div>
          {props?.approvalType === "Client" && (
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
              <label htmlFor="" className="font-sm text-uppercase">
                Contact Type
              </label>
              <Controller
                control={control}
                rules={{ required: false }}
                name="isVerified"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={isVerified}
                    textField={"name"}
                    valueField={"value"}
                    placeholder={"Select contact Type"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />
              {errors.isVerified && <ErrorMessage>Contact Type is required</ErrorMessage>}
            </div>
          )}
        </div>
        <div className="row">
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Title<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="prefix"
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={title}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select prefix"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                />
              )}
            />
            {errors.prefix && <ErrorMessage>Prefix is required</ErrorMessage>}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              First Name<span className="text-danger">*</span>
            </label>

            <Controller
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  return !!ValidateAlphabets(value);
                },
                maxLength: 25,
              }}
              name="firstName"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="First Name"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="firstName"
                />
              )}
            />
            {errors.firstName && errors.firstName.type === "required" && (
              <ErrorMessage>First Name is required</ErrorMessage>
            )}
            {errors.firstName && errors.firstName.type === "maxLength" && (
              <ErrorMessage>First name should contains maximum 25 characters</ErrorMessage>
            )}
            {errors.firstName && errors.firstName.type === "validate" && (
              <ErrorMessage>Invalid First Name</ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Last Name
            </label>
            <Controller
              control={control}
              name="lastName"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Last Name"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="lastName"
                />
              )}
            />
            {errors.lastName && errors.lastName.type === "required" && (
              <ErrorMessage>Last Name is required</ErrorMessage>
            )}
            {errors.lastName && errors.lastName.type === "maxLength" && (
              <ErrorMessage>Last name should contains maximum 25 characters</ErrorMessage>
            )}
            {errors.lastName && errors.lastName.type === "validate" && (
              <ErrorMessage>Invalid Last Name</ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Phone
            </label>
            <Controller
              control={control}
              rules={{
                validate: () => {
                  if (
                    selectedPhone?.length &&
                    selectedPhone?.every(
                      (x) =>
                        x.phone === x.dialCode || x.phone === `+${x.dialCode}` || x.phone === ""
                    )
                  ) {
                    return true;
                  }
                  return !(
                    (
                      selectedPhone &&
                      selectedPhone?.filter((x) => Validate965DigitPhoneNumber(x.phone)).length !==
                        selectedPhone?.length
                    )
                  );
                },
              }}
              name="phones"
              defaultValue={props.phones}
              render={({ onChange }) => (
                <CustomerPhone
                  className="form-control p-0 rounded-sm font-weight-semibold"
                  placeholder="Phone"
                  value={selectedPhone}
                  onChange={(val) => {
                    onChangePhone(val);
                    onChange(val);
                  }}
                />
              )}
            />
            {errors.phones && (
              <div>
                <div>
                  {!selectedPhone ? (
                    <ErrorMessage>Valid Phone is required</ErrorMessage>
                  ) : (
                    <>
                      {selectedPhone?.filter(
                        (x) => !!(x.phone.startsWith("91") || x.phone.startsWith("+91"))
                      ).length > 0 ? (
                        <ErrorMessage>Phone number should be 10 digits</ErrorMessage>
                      ) : (
                        <ErrorMessage>Invalid Phone number</ErrorMessage>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase d-flex align-items-end">
              Email
            </label>
            <Controller
              control={control}
              rules={{
                validate: () => {
                  if (selectedEmail?.length && selectedEmail?.every((x) => !x.email)) {
                    return true;
                  } else {
                    return !(
                      (
                        selectedEmail &&
                        selectedEmail?.filter((x) => ValidateEmail(x.email)).length === 0 &&
                        isEmailEnable
                      )
                    );
                  }
                },
              }}
              name="emails"
              defaultValue={props.emails}
              render={() => (
                <CustomerEmail
                  className="form-control p-0 rounded-sm font-weight-semibold"
                  placeholder="Email"
                  value={selectedEmail}
                  onChange={onChangeEmail}
                  disabled={!isEmailEnable}
                />
              )}
            />
            {errors.emails && (
              <div>
                <ErrorMessage>Valid email is required</ErrorMessage>
              </div>
            )}
          </div>

          {props?.approvalType === "Client" && (
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
              <label htmlFor="" className="font-sm text-uppercase">
                Designation
              </label>
              <Controller
                control={control}
                name="designationId"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={designations?.map((key) => {
                      return {
                        _id: key._id,
                        name: key.name,
                      };
                    })}
                    textField={"name"}
                    valueField={"_id"}
                    filter="contains"
                    placeholder={"Select Designation"}
                    onChange={(val) => {
                      onChange(val._id);
                    }}
                  />
                )}
              />
            </div>
          )}

          {props?.approvalType === "Client" && (
            <>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Contact Category
                </label>
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="customerContactType"
                  render={({ onChange, value }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={customerContactType}
                      textField={"name"}
                      valueField={"value"}
                      placeholder={"Select contact category"}
                      onChange={(val) => {
                        onChange(val.value);
                      }}
                    />
                  )}
                />
                {errors.customerContactType && (
                  <ErrorMessage>Contact Category is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Name
                </label>

                <Controller
                  control={control}
                  name="assistantName"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Assistant Name"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="assistantName"
                    />
                  )}
                />
                {errors.assistantName && <ErrorMessage>Assistant Name is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Number
                </label>

                <Controller
                  control={control}
                  rules={{
                    required: false,
                    validate: (value) =>
                      !ValidatePhoneNumberRegEx(value || "") && !!value
                        ? "Invalid phone"
                        : undefined,
                  }}
                  name="assistantNumber"
                  render={({ onChange, value }) => (
                    <>
                      <PhoneInput
                        country={"in"}
                        value={`${value}`}
                        onChange={(phone) => onChange(`${phone}`)}
                      />
                    </>
                  )}
                />
                {errors.assistantNumber?.message && (
                  <ErrorMessage>{errors?.assistantNumber?.message}</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Designation
                </label>

                <Controller
                  control={control}
                  name="assistantDesignation"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Assistant Designation"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="assistantDesignation"
                    />
                  )}
                />
                {errors.assistantDesignation && (
                  <ErrorMessage>Assistant Designation is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Seniority
                </label>
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="seniority"
                  render={({ onChange, value }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={seniorityDetails}
                      textField={"title"}
                      valueField={"_id"}
                      placeholder={"Select Seniority"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.seniority && <ErrorMessage>Seniority is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Department
                </label>

                <Controller
                  control={control}
                  name="department"
                  render={({ onChange, value }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={departmentDetails}
                      textField={"title"}
                      valueField={"_id"}
                      placeholder={"Enter Department"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.department && <ErrorMessage>Department is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Association Start Date
                </label>
                <Controller
                  control={control}
                  name="associationStartDate"
                  render={({ onChange, value }) => (
                    <DateInput
                      default={value}
                      placeholder={"Association Start Date"}
                      onChange={onChange}
                      {...(watch("associationEndDate") !== undefined
                        ? { maxDate: new Date(watch("associationEndDate") || "") }
                        : {})}
                    />
                  )}
                />
                {errors.associationStartDate && (
                  <ErrorMessage>Association Start Date is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Association End Date
                </label>
                <Controller
                  control={control}
                  name="associationEndDate"
                  render={({ onChange, value }) => (
                    <DateInput
                      default={value}
                      placeholder={"Association End Date"}
                      onChange={onChange}
                      {...(watch("associationStartDate") !== undefined
                        ? { minDate: new Date(watch("associationStartDate") || "") }
                        : {})}
                    />
                  )}
                />
                {errors.associationEndDate && (
                  <ErrorMessage>Association End Date is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Director With Company
                </label>
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="isDirectorOfCompany"
                  render={({ onChange, value }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={isDirectorOfCompany}
                      textField={"name"}
                      valueField={"value"}
                      placeholder={"Select Director With Company"}
                      onChange={(val) => {
                        onChange(val.value);
                      }}
                    />
                  )}
                />
                {errors.isDirectorOfCompany && (
                  <ErrorMessage>Director With Company is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Account Manager
                </label>
                <Controller
                  control={control}
                  name="accountManager"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Account Manager"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="accountManager"
                    />
                  )}
                />
                {errors.accountManager && <ErrorMessage>Account Manager is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Remarks
                </label>
                <Controller
                  control={control}
                  name="remarks"
                  render={({ onChange, value, name }) => (
                    <textarea
                      rows={3}
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Remarks"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="remarks"
                    />
                  )}
                />
                {errors.remarks && <ErrorMessage>remarks is required</ErrorMessage>}
              </div>
            </>
          )}
        </div>

        {props?.approvalType === "Client" && (
          <div className="row">
            <hr className="" />
            <h5 className="text-uppercase font-weight-semibold mb-2 col-12">Address</h5>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                Address Type
              </label>
              <Controller
                control={control}
                name="addressType"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={addressType}
                    textField={"name"}
                    valueField={"value"}
                    placeholder={"Select address Type"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />
              {errors.addressType && <ErrorMessage>Address Type is required</ErrorMessage>}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                Address
              </label>
              <Controller
                control={control}
                name="address"
                render={({ onChange, value, name }) => (
                  <textarea
                    rows={3}
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Address"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="address"
                  />
                )}
              />
              {errors.address && <ErrorMessage>Address is required</ErrorMessage>}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                Country
              </label>
              <Controller
                control={control}
                name="country"
                defaultValue="India"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={Countries}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select country"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                    filter={"contains"}
                  />
                )}
              />
              {errors.country && (
                <ErrorMessage>
                  {errors.country.type === "required" ? "Country is required" : "Invalid Country"}
                </ErrorMessage>
              )}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                State 
              </label>
              <Controller
                control={control}
                name="state"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={statesList}
                    textField={"state"}
                    valueField={"state"}
                    placeholder={"Select state"}
                    onChange={(val) => {
                      onChange(val.state);
                      setDistrictList(val.districts);
                    }}
                  />
                )}
              />
              {errors.state && (
                <ErrorMessage>
                  {errors.state.type === "required" ? "State is required" : "Invalid State"}
                </ErrorMessage>
              )}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                District 
              </label>
              <Controller
                control={control}
                name="district"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={districtList?.map((d) => {
                      return {
                        name: d,
                      };
                    })}
                    textField={"name"}
                    valueField={"name"}
                    placeholder={"Select district"}
                    onChange={(val) => {
                      onChange(val.name);
                    }}
                  />
                )}
              />
              {errors.district && (
                <ErrorMessage>
                  {errors.district.type === "required"
                    ? "District is required"
                    : "Invalid District"}
                </ErrorMessage>
              )}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                City
              </label>

              <Controller
                control={control}
                name="city"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="City"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="city"
                  />
                )}
              />
              {errors.city && (
                <ErrorMessage>
                  {errors.city.type === "required" ? "City is required" : "Invalid City"}
                </ErrorMessage>
              )}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                Zip code
              </label>
              <Controller
                control={control}
                name="postalCode"
                render={({ onChange, value, name }) => (
                  <input
                    type="text"
                    className="form-control rounded-sm font-weight-semibold"
                    placeholder="Postal Code"
                    name={name}
                    value={value}
                    onChange={onChange}
                    id="postalCode"
                  />
                )}
              />
              {errors.postalCode && (
                <ErrorMessage>
                  {errors.postalCode.type === "required"
                    ? "Postal Code is required"
                    : "Invalid Postal Code"}
                </ErrorMessage>
              )}
            </div>
            <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle} `}>
              <label htmlFor="" className="font-sm text-uppercase">
                Contact Status
              </label>
              <Controller
                control={control}
                name="contactStatus"
                render={({ onChange, value }) => (
                  <DropdownList
                    value={value}
                    defaultValue={value}
                    data={contactStatus}
                    textField={"name"}
                    valueField={"value"}
                    placeholder={"Select contact status"}
                    onChange={(val) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />
              {errors.contactStatus && <ErrorMessage>Contact Status is required</ErrorMessage>}
            </div>
          </div>
        )}

        {isEmailOrPhone ? (
          <ErrorMessage>At least one of email or phone is required</ErrorMessage>
        ) : (
          ""
        )}
        <div className="col-12 font-normal font-weight-semibold pt-3 mb-2 border-top mt-3 pb-5">
          <button
            type="button"
            onClick={() => {
              reset();
              props.onClose?.();
            }}
            className="btn btn-outline-secondary font-base font-weight-bold mr-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary font-base font-weight-bold px-5 rounded"
            disabled={isSubmiting}
          >
            Add User
          </button>
          <br />
        </div>
      </div>
    </form>
  );
};

export default AddNewUserModal;
